<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-6">
                        <div class="flex justify-between items-center flex-wrap gap-4">
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">Flex Groups</h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">Manage flex groups for your application</p>
                                </div>
                                <div class="flex items-center gap-2">
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer transition-colors"
                                                :disabled="isLoading"
                                                @click="loadData"
                                        >
                                                <svg class="w-4 h-4" :class="{ 'animate-spin': isLoading }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                Refresh
                                        </button>
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 hover:bg-orange-6 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors"
                                                @click="showCreateModal = true"
                                        >
                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                                </svg>
                                                Create Flex Group
                                        </button>
                                </div>
                        </div>
                </header>

                <!-- Main Content -->
                <main class="p-6">
                        <!-- Search Card -->
                        <div class="bg-white rounded-xl shadow-sm py-4 px-6 mb-4">
                                <div class="flex flex-wrap gap-4 items-center">
                                        <div class="flex-1 min-w-[12rem]">
                                                <div class="relative">
                                                        <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                        </svg>
                                                        <input
                                                                v-model="searchQuery"
                                                                type="text"
                                                                placeholder="Search flex groups..."
                                                                class="w-full py-2 px-3 pl-9 border border-gray-300 rounded-lg text-sm outline-none"
                                                        >
                                                </div>
                                        </div>
                                        <div class="flex items-center gap-2">
                                                <span class="text-xs bg-orange-2 text-orange-7 py-1 px-3 rounded-full font-medium">
                                                        {{ filteredItems.length }} flex groups
                                                </span>
                                        </div>
                                </div>
                        </div>

                        <!-- Loading State -->
                        <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                                <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span class="ml-3 text-gray-500 text-sm">Loading flex groups...</span>
                        </div>

                        <!-- Table Card -->
                        <div v-else class="bg-white rounded-xl shadow-sm overflow-hidden">
                                <div class="overflow-x-auto">
                                        <table class="w-full border-collapse">
                                                <thead>
                                                        <tr class="bg-gray-50">
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 w-20">ID</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Name</th>
                                                                <th class="py-3 px-4 text-right text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 w-32">Actions</th>
                                                        </tr>
                                                </thead>
                                                <tbody>
                                                        <tr v-if="paginatedItems.length === 0">
                                                                <td colspan="3" class="text-center py-12 px-4">
                                                                        <svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                                                        </svg>
                                                                        <p class="text-gray-500 text-sm m-0">No flex groups found</p>
                                                                        <p class="text-gray-400 text-xs mt-1 mb-0">Create a new flex group to get started</p>
                                                                </td>
                                                        </tr>
                                                        <tr
                                                                v-for="item in paginatedItems"
                                                                :key="item.id"
                                                                class="border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                                                                @click="editItem(item.id)"
                                                        >
                                                                <td class="py-3 px-4 text-sm text-gray-500 font-mono">{{ item.id }}</td>
                                                                <td class="py-3 px-4 text-sm text-gray-900 font-medium">{{ item.name }}</td>
                                                                <td class="py-3 px-4 text-right">
                                                                        <div class="flex justify-end gap-2">
                                                                                <button
                                                                                        type="button"
                                                                                        class="p-1.5 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-orange-7"
                                                                                        title="Edit"
                                                                                        @click.stop="editItem(item.id)"
                                                                                >
                                                                                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                                        </svg>
                                                                                </button>
                                                                                <button
                                                                                        type="button"
                                                                                        class="p-1.5 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-red-600"
                                                                                        title="Delete"
                                                                                        @click.stop="confirmDelete(item)"
                                                                                >
                                                                                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                                        </svg>
                                                                                </button>
                                                                        </div>
                                                                </td>
                                                        </tr>
                                                </tbody>
                                        </table>
                                </div>

                                <!-- Pagination -->
                                <div v-if="totalPages > 1" class="flex justify-between items-center py-4 px-6 border-t border-gray-200">
                                        <div class="text-sm text-gray-500">
                                                Showing {{ startRow }} to {{ endRow }} of {{ filteredItems.length }} flex groups
                                        </div>
                                        <div class="flex gap-2">
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === 1"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="currentPage--"
                                                >
                                                        Previous
                                                </button>
                                                <button
                                                        v-for="page in visiblePages"
                                                        :key="page"
                                                        type="button"
                                                        class="py-2 px-3 border rounded-md text-sm cursor-pointer min-w-[2.5rem]"
                                                        :class="page === currentPage ? 'bg-orange-7 text-white border-orange-7' : 'bg-white text-gray-700 border-gray-300'"
                                                        @click="currentPage = page"
                                                >
                                                        {{ page }}
                                                </button>
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === totalPages"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="currentPage++"
                                                >
                                                        Next
                                                </button>
                                        </div>
                                </div>
                        </div>
                </main>

                <!-- Create Modal -->
                <div v-if="showCreateModal" class="fixed inset-0 z-50 flex items-center justify-center">
                        <div class="absolute inset-0 bg-black/50" @click="showCreateModal = false"></div>
                        <div class="relative bg-white rounded-xl shadow-2xl w-full max-w-md m-4">
                                <div class="flex justify-between items-center py-4 px-6 border-b border-gray-200">
                                        <h3 class="text-lg font-semibold text-gray-900 m-0">Create New Flex Group</h3>
                                        <button
                                                type="button"
                                                class="text-gray-400 bg-transparent border-none cursor-pointer p-1"
                                                @click="showCreateModal = false"
                                        >
                                                <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                        </button>
                                </div>
                                <div class="p-6">
                                        <div class="mb-4">
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Flex Group Name</label>
                                                <input
                                                        v-model="newItemName"
                                                        type="text"
                                                        placeholder="Enter flex group name"
                                                        class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border"
                                                        @keydown.enter="createItem"
                                                >
                                        </div>
                                </div>
                                <div class="flex justify-end gap-3 py-4 px-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                                        <button
                                                type="button"
                                                class="py-2.5 px-4 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm cursor-pointer"
                                                @click="showCreateModal = false"
                                        >
                                                Cancel
                                        </button>
                                        <button
                                                type="button"
                                                :disabled="!newItemName.trim() || isCreating"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 text-white border-none rounded-lg text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                @click="createItem"
                                        >
                                                <svg v-if="isCreating" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                                </svg>
                                                {{ isCreating ? 'Creating...' : 'Create' }}
                                        </button>
                                </div>
                        </div>
                </div>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { FlexGroupModel } from '@sosocio/creator/interfaces';
import Swal from 'sweetalert2';
import FlexGroupService from 'services/FlexGroupService';

export default defineComponent({
        name: 'FlexGroupPage',
        setup() {
                const router = useRouter();

                const items = ref<FlexGroupModel[]>([]);
                const searchQuery = ref('');
                const currentPage = ref(1);
                const pageSize = ref(25);
                const isLoading = ref(true);
                const showCreateModal = ref(false);
                const newItemName = ref('');
                const isCreating = ref(false);

                const filteredItems = computed(() => {
                        if (!searchQuery.value.trim()) {
                                return items.value;
                        }
                        const query = searchQuery.value.toLowerCase();
                        return items.value.filter(item =>
                                String(item.name ?? '').toLowerCase().includes(query) ||
                                String(item.id).includes(query)
                        );
                });

                const totalRows = computed(() => filteredItems.value.length);
                const totalPages = computed(() => Math.max(1, Math.ceil(totalRows.value / pageSize.value)));
                const startRow = computed(() => totalRows.value === 0 ? 0 : (currentPage.value - 1) * pageSize.value + 1);
                const endRow = computed(() => Math.min(currentPage.value * pageSize.value, totalRows.value));

                const paginatedItems = computed(() => {
                        const start = (currentPage.value - 1) * pageSize.value;
                        return filteredItems.value.slice(start, start + pageSize.value);
                });

                const visiblePages = computed(() => {
                        const pages: number[] = [];
                        const maxVisible = 5;
                        let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2));
                        const end = Math.min(totalPages.value, start + maxVisible - 1);
                        start = Math.max(1, end - maxVisible + 1);
                        for (let i = start; i <= end; i++) {
                                pages.push(i);
                        }
                        return pages;
                });

                watch([searchQuery], () => {
                        currentPage.value = 1;
                });

                const loadData = async () => {
                        isLoading.value = true;
                        try {
                                const params = new URLSearchParams({ limit: '0' });
                                const data = await FlexGroupService.getAll(params);
                                items.value = data;
                        } catch (error: any) {
                                console.error('Failed to load flex groups:', error);
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to load flex groups',
                                        icon: 'error',
                                });
                        } finally {
                                isLoading.value = false;
                        }
                };

                const editItem = (id: number) => {
                        router.push(`/flexgroup/${id}`);
                };

                const confirmDelete = async (item: FlexGroupModel) => {
                        const result = await Swal.fire({
                                title: 'Delete Flex Group?',
                                text: `Are you sure you want to delete "${item.name || 'this item'}"? This action cannot be undone.`,
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonColor: '#dc2626',
                                cancelButtonColor: '#6b7280',
                                confirmButtonText: 'Yes, delete it',
                                cancelButtonText: 'Cancel',
                        });

                        if (result.isConfirmed) {
                                try {
                                        await FlexGroupService.delete(item.id);
                                        items.value = items.value.filter(i => i.id !== item.id);
                                        Swal.fire({
                                                toast: true,
                                                position: 'top-end',
                                                icon: 'success',
                                                title: 'Flex group deleted successfully',
                                                showConfirmButton: false,
                                                timer: 3000,
                                        });
                                } catch (error: any) {
                                        Swal.fire({
                                                title: 'Error',
                                                text: error.message || 'Failed to delete flex group',
                                                icon: 'error',
                                        });
                                }
                        }
                };

                const createItem = async () => {
                        if (!newItemName.value.trim()) return;

                        isCreating.value = true;
                        try {
                                const newFlexGroup = await FlexGroupService.create({
                                        name: newItemName.value.trim(),
                                });
                                showCreateModal.value = false;
                                newItemName.value = '';
                                router.push(`/flexgroup/${newFlexGroup.id}`);
                        } catch (error: any) {
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to create flex group',
                                        icon: 'error',
                                });
                        } finally {
                                isCreating.value = false;
                        }
                };

                onMounted(() => {
                        loadData();
                });

                return {
                        items,
                        searchQuery,
                        currentPage,
                        pageSize,
                        isLoading,
                        showCreateModal,
                        newItemName,
                        isCreating,
                        filteredItems,
                        totalRows,
                        totalPages,
                        startRow,
                        endRow,
                        paginatedItems,
                        visiblePages,
                        editItem,
                        confirmDelete,
                        createItem,
                        loadData,
                };
        },
});
</script>
